/**
 * Who is here, and which face of the product they arrived at.
 *
 * One call answers both, for a signed-out visitor as well as a signed-in one:
 * the marketing site and the login page both need to know whether this hostname
 * is the apex or a workspace, and a tenant hostname must give nothing away
 * either way.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api, describeError } from './api';

export interface Viewer {
  accountId: string;
  email: string;
  displayName: string | null;
  isPlatformAdmin: boolean;
}

/** The nine colours the console paints itself with. */
export type ThemeTokens = Record<string, string>;

export interface TenantBrand {
  displayName: string | null;
  logoUrl: string | null;
  accent: string | null;
  /**
   * Already resolved by the server: the named palette, then any imported brand
   * template, then the accent. The browser paints what it is given rather than
   * reimplementing that precedence and drifting from it.
   */
  theme?: ThemeTokens;
  palette?: string;
}

export interface TenantSummary {
  slug: string;
  name: string;
  state: string;
  branding: TenantBrand;
}

export interface Identity {
  signedIn: boolean;
  host: string;
  /** True at the apex and anywhere that is not a subdomain of it. */
  marketing: boolean;
  apex: string;
  platformOrigin: string;
  tenant: TenantSummary | null;
  /**
   * What this workspace has, by feature id. Absent at the apex, where there is
   * no workspace to have anything.
   *
   * The navigation reads it to decide what to offer. It is **not** the boundary:
   * the API refuses on its own, because a hidden menu item is a courtesy and a
   * URL anybody can type is not.
   */
  features?: Record<string, boolean>;
  user?: Viewer;
  csrf?: string;
}

interface SessionValue {
  identity: Identity | null;
  loading: boolean;
  error: string | null;
  /** Re-read after anything that changes who you are or what you may do. */
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIdentity(await api.get<Identity>('/api/auth/me'));
      setError(null);
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      // The server ends its own session and hands back where to go next, which
      // may be Keycloak's end-session endpoint so the identity session goes too.
      const result = await api.post<{ redirectTo: string }>('/api/auth/logout');
      window.location.href = result.redirectTo;
    } catch {
      window.location.href = '/';
    }
  }, []);

  const value = useMemo(
    () => ({ identity, loading, error, refresh, signOut }),
    [identity, loading, error, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider.');
  return value;
}

/**
 * Send an unauthenticated visitor to sign in, through the silent chain first.
 *
 * Keycloak may still hold the session from wherever this person last signed in
 * on the platform, so on a hostname they have never visited the whole ceremony
 * is two redirects and no interaction. Only when Keycloak says it has nothing to
 * reuse do they see a login page.
 */
export function goSignIn(returnTo: string, { silent = true } = {}): void {
  const target = encodeURIComponent(returnTo);
  window.location.href = silent
    ? `/api/auth/login?silent=1&returnTo=${target}`
    : `/api/auth/login?returnTo=${target}`;
}
